import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Users, UserCheck, UserX, Search, Edit, Trash2, RefreshCw, MapPin } from 'lucide-react'
import api from '../../lib/api'
import '../Pages.css'

function Contacts() {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  const fetchContacts = () => {
    setLoading(true)
    api.get('/contacts')
      .then((res) => {
        setContacts(res.data)
      })
      .catch((err) => {
        console.error('Error fetching contacts:', err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchContacts()
  }, [])

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this contact?')) return
    try {
      await api.delete(`/contacts/${id}`)
      setContacts(prev => prev.filter(c => c.id !== id))
    } catch (err) {
      console.error('Error deleting contact:', err)
    }
  }

  const filteredContacts = contacts.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.location || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  const subscribedCount = contacts.filter(c => !c.unsubscribed).length
  const unsubscribedCount = contacts.filter(c => c.unsubscribed).length

  return (
    <div className="page contacts-page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Contacts</h2>
          <p>Manage your contact list and import new contacts</p>
        </div>
        <Link to="/contacts/add" className="btn btn-primary">
          <Plus size={18} /><span>Add Contact</span>
        </Link>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}><Users size={24} /></div>
          <div className="stat-content"><span className="stat-value">{contacts.length}</span><span className="stat-label">Total Contacts</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}><UserCheck size={24} /></div>
          <div className="stat-content"><span className="stat-value">{subscribedCount}</span><span className="stat-label">Subscribed</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}><UserX size={24} /></div>
          <div className="stat-content"><span className="stat-value">{unsubscribedCount}</span><span className="stat-label">Unsubscribed</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><MapPin size={24} /></div>
          <div className="stat-content"><span className="stat-value">{new Set(contacts.map(c => c.location).filter(Boolean)).size}</span><span className="stat-label">Locations</span></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>All Contacts</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div className="search-box">
              <Search size={16} className="search-icon" />
              <input type="text" placeholder="Search contacts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <button className="btn btn-ghost" onClick={fetchContacts}><RefreshCw size={16} /><span>Refresh</span></button>
          </div>
        </div>
        <div className="table-container">
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading contacts...</div>
          ) : filteredContacts.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              {searchTerm ? 'No contacts match your search.' : 'No contacts yet. Import contacts to get started.'}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact, index) => (
                  <tr key={contact.id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="email-cell">
                        <div className="email-avatar">{(contact.name || contact.email || '?').charAt(0).toUpperCase()}</div>
                        <span>{contact.name || '-'}</span>
                      </div>
                    </td>
                    <td>{contact.email}</td>
                    <td>{contact.location ? <span className="provider-badge">{contact.location}</span> : '-'}</td>
                    <td>
                      <span className="status-badge" style={{
                        background: contact.unsubscribed ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                        color: contact.unsubscribed ? '#ef4444' : '#22c55e'
                      }}>
                        <span className="pulse-dot"></span>
                        <span>{contact.unsubscribed ? 'Unsubscribed' : 'Subscribed'}</span>
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="icon-btn" title="Edit"><Edit size={16} /></button>
                        <button className="icon-btn danger" title="Delete" onClick={() => handleDelete(contact.id)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default Contacts
